import { useQuery } from '@apollo/client/react';
import { type HTMLAttributes, useMemo } from 'react';
import { useCurrentUser, useUserActions, useUserNotifications } from 'stores/useUserStore';
import styled from 'styled-components';

import AddEntityNavComponent from 'components/core/navigation/operations/AddEntityNavComponent';
import FeedbackMenuNavItem from 'components/core/navigation/operations/FeedbackMenuNavItem';
import UserMenuNavItem from 'components/core/navigation/operations/UserMenuNavItem';
import TitledNavLinkItem from 'components/core/navigation/TitledNavLinkItem';
import TopNavLinkItem from 'components/core/navigation/TopNavLinkItem';
import {
  AppointmentsIcon,
  CogIcon,
  ConversationsIcon,
  GlobeIcon,
  IncomingIcon,
  PersonSearchIcon,
  RooftopsIcon,
  TasksIcon,
  TradeInIcon,
  UsersIcon,
  VehicleIcon,
} from 'components/ui/icons';
import { FeatureBundleSet } from 'enums/featureBundle';
import { RoutePath } from 'enums/routePath';
import { ElementTestId } from 'enums/testing';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useRouter } from 'hooks/useRouter';
import { doUserRooftopsHaveFeatureEnabled } from 'utils/featureBundleRooftopUtils';
import { translate } from 'utils/intlUtils';
import {
  fullGroupOrAboveScopes,
  hasMinimumScope,
  nonWhiteLabelScopes,
  ROOFTOPS_BASIC_PERMISSIONS,
} from 'utils/permissionUtils';

import { userFeatureBundlesQuery } from 'store/api/graph/queries/user';
import { ResourceType, UserScope } from 'store/client/graphql';

import { GLOBAL_NAV_WIDTH } from 'styles/layouts';
import { NEUTRAL_800, NEUTRAL_900, SPACE_300, SPACE_600, WIDTH_600 } from 'styles/tokens';

const GlobalNavContainer = styled.nav`
  background: ${NEUTRAL_800};
  display: flex;
  flex-direction: column;
  width: ${GLOBAL_NAV_WIDTH};
`;

const NavWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: ${SPACE_300};
  row-gap: ${SPACE_600};
`;

const NavContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${SPACE_300};
`;

const NavItemSeparator = styled.div`
  background-color: ${NEUTRAL_900};
  height: 1px;
  margin: 0 auto;
  width: ${WIDTH_600};
`;

const GlobalNav = (props: HTMLAttributes<HTMLElement>) => {
  const { t } = translate;
  const { id, scope } = useCurrentUser();
  const { hasIncomingLeadActivities, hasUnreadConversations } = useUserNotifications();
  const { hasPermissions } = useUserActions();
  const { flags } = useFeatureFlags();
  const { pathname } = useRouter();

  const { data: userFeatureBundlesData, loading } = useQuery(userFeatureBundlesQuery);

  const userRooftops = userFeatureBundlesData?.user?.rooftops;

  const features = useMemo(
    () => ({
      appointmentEnabled: doUserRooftopsHaveFeatureEnabled(FeatureBundleSet.APPOINTMENT, userRooftops),
      conversationEnabled: doUserRooftopsHaveFeatureEnabled(FeatureBundleSet.CONVERSATION, userRooftops),
      leadEnabled: doUserRooftopsHaveFeatureEnabled(FeatureBundleSet.LEAD, userRooftops),
      retailEnabled: doUserRooftopsHaveFeatureEnabled(FeatureBundleSet.RETAIL, userRooftops),
      taskEnabled: doUserRooftopsHaveFeatureEnabled(FeatureBundleSet.TASK, userRooftops),
      tradeInEnabled: doUserRooftopsHaveFeatureEnabled(FeatureBundleSet.TRADE_IN, userRooftops),
    }),
    [userRooftops]
  );

  if (!id || loading) {
    return (
      <GlobalNavContainer {...props}>
        <NavContainer>
          <TopNavLinkItem to={RoutePath.HOME} />
        </NavContainer>
      </GlobalNavContainer>
    );
  }

  return (
    <GlobalNavContainer {...props}>
      <TopNavLinkItem
        disabled={!hasPermissions(ROOFTOPS_BASIC_PERMISSIONS)}
        to={flags.dashboardEnabled ? RoutePath.HOME : RoutePath.RETAIL_ITEMS}
      />
      <NavWrapper>
        <NavContainer>
          <AddEntityNavComponent />
          <NavItemSeparator />
          {features.retailEnabled ? (
            <TitledNavLinkItem
              icon={<VehicleIcon />}
              resourceType={ResourceType.RETAIL_ITEMS}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_RETAIL_ITEM}
              title={t('retail_inventory')}
              to={RoutePath.RETAIL_ITEMS}
            />
          ) : null}
          {features.tradeInEnabled ? (
            <TitledNavLinkItem
              icon={<TradeInIcon />}
              resourceType={ResourceType.TRADE_IN_ITEMS}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_TRADE_IN}
              title={t('trade_in_inventory')}
              to={RoutePath.TRADE_IN_ITEMS}
            />
          ) : null}

          {features.leadEnabled ? (
            <TitledNavLinkItem
              hasNotification={hasIncomingLeadActivities}
              icon={<IncomingIcon />}
              resourceType={ResourceType.LEADS}
              scope={nonWhiteLabelScopes}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_LEAD_ACTIVITY}
              title={t('incoming')}
              to={RoutePath.LEAD_ACTIVITIES}
            />
          ) : null}

          {features.leadEnabled ? (
            <TitledNavLinkItem
              icon={<PersonSearchIcon />}
              resourceType={ResourceType.LEADS}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_LEAD}
              title={t('lead_other')}
              to={RoutePath.LEADS}
            />
          ) : null}
          {features.conversationEnabled ? (
            <TitledNavLinkItem
              hasNotification={hasUnreadConversations}
              icon={<ConversationsIcon />}
              resourceType={ResourceType.LEADS}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_CONVERSATION}
              title={t('conversations')}
              to={RoutePath.CONVERSATIONS}
            />
          ) : null}
          {features.appointmentEnabled ? (
            <TitledNavLinkItem
              icon={<AppointmentsIcon />}
              resourceType={ResourceType.APPOINTMENTS}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_APPOINTMENT}
              title={t('appointments_other')}
              to={RoutePath.APPOINTMENTS}
            />
          ) : null}
          {features.taskEnabled ? (
            <TitledNavLinkItem
              icon={<TasksIcon />}
              resourceType={ResourceType.TASKS}
              testId={ElementTestId.GLOBAL_NAV_MENU_LINK_TASK}
              title={t('tasks_other')}
              to={RoutePath.TASKS}
            />
          ) : null}
        </NavContainer>
        <NavContainer>
          <TitledNavLinkItem
            icon={<GlobeIcon />}
            resourceType={ResourceType.WEBSITES}
            scope={fullGroupOrAboveScopes}
            title={t('websites')}
            to={RoutePath.WEBSITES}
          />
          <TitledNavLinkItem
            forceDisabled={pathname.indexOf(RoutePath.SETTINGS) === 0 && !hasMinimumScope(scope, UserScope.GLOBAL)}
            icon={<RooftopsIcon />}
            resourceType={ResourceType.ROOFTOPS}
            testId={ElementTestId.GLOBAL_NAV_MENU_LINK_ROOFTOPS}
            title={t('rooftops_other')}
            to={RoutePath.ROOFTOPS}
          />
          <TitledNavLinkItem
            icon={<UsersIcon />}
            resourceType={ResourceType.USERS}
            title={t('users')}
            to={RoutePath.USERS}
          />
          <TitledNavLinkItem icon={<CogIcon />} title={t('settings')} to={RoutePath.SETTINGS} />
          <NavItemSeparator />
          <UserMenuNavItem />
          <FeedbackMenuNavItem title={t('help')} />
        </NavContainer>
      </NavWrapper>
    </GlobalNavContainer>
  );
};

export default GlobalNav;
